import { Contract, Repetition, Unit, WorkingTimeClassicType } from "../../__generated__/graphql";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFormatWorkingTimeClassicSpec, useI18n } from "../../hooks/i18n-hooks";
import VStack from "../../ui_atoms/VStack";

import SelectField from "../SelectField";
import {
    ON_DEMAND_WORKING_TIME_CLASSIC_SPEC,
    setContractWorkingTimeClassicSpec,
    useHasErrorWorkingTimeClassicSpec,
    WorkingTimeComponentProps,
} from "./helpers";
import { BodyMediumText, BodySmallText } from "../FormattedText";
import Gap from "../../ui_atoms/Gap";
import { Buildable } from "ts-essentials";
import { useTheme } from "../../hooks/use-theme";
import { MAX_HOURS_PER_WEEK } from "../../utils";
import OptionsField from "../OptionsField";
import QuantityField from "../QuantityField";
import { useContractCct } from "../../hooks/cct-utils";

type HourlyMode = "recurrent" | "punctual" | "on_demand";

const getHourlyMode = (repetition?: Repetition | null): HourlyMode => {
    switch (repetition) {
        case Repetition.None:
            return "punctual";
        case Repetition.OnDemand:
            return "on_demand";
        default:
            return "recurrent";
    }
};

const HourlyClassicComponent: React.FC<WorkingTimeComponentProps> = ({
    disabled = false,
    contract,
    setContract,
    allowOnDemand = false,
}) => {
    const { colors } = useTheme();
    const i18n = useI18n();
    const workingTime = contract.workingTimeClassicSpec;
    const repetition = contract.workingTimeClassicSpec?.repetition;
    const [mode, setMode] = useState(getHourlyMode(contract.workingTimeClassicSpec?.repetition));
    const unitCount = workingTime?.unitCount || undefined;

    const setter = useCallback(
        (v: { workingTime: Buildable<WorkingTimeClassicType> }) =>
            setContract({
                ...setContractWorkingTimeClassicSpec(contract, v.workingTime),
            }),
        [setContract, contract],
    );

    useEffect(() => {
        if (!contract.workingTimeClassicSpec) {
            setContract({
                ...contract,
                workingTimeClassicSpec: {
                    repetition: Repetition.Week,
                    unitCount: undefined,
                    unit: Unit.Hours,
                },
            });
        }
    }, [contract, setContract]);

    const repetitionItems = useMemo(() => {
        return [
            {
                label: i18n.t("utils.working_time.repetition_for_hourly.week"),
                value: Repetition.Week,
            },
            {
                label: i18n.t("utils.working_time.repetition_for_hourly.week2"),
                value: Repetition.Week2,
            },
            {
                label: i18n.t("utils.working_time.repetition_for_hourly.week3"),
                value: Repetition.Week3,
            },
            {
                label: i18n.t("utils.working_time.repetition_for_hourly.week4"),
                value: Repetition.Week4,
            },
            {
                label: i18n.t("utils.working_time.repetition_for_hourly.month"),
                value: Repetition.Month,
            },
        ];
    }, [i18n]);
    const formatWorkingTime = useFormatWorkingTimeClassicSpec();
    const hasErrorWorkingTimeClassicSpec = useHasErrorWorkingTimeClassicSpec();
    const missionCct = useContractCct(contract as Contract);
    const { errorUnitCount } = useMemo(() => {
        const hoursPerWeek = missionCct?.hoursPerWeek || MAX_HOURS_PER_WEEK;
        return hasErrorWorkingTimeClassicSpec(
            Unit.Hours,
            repetition,
            Number(unitCount),
            hoursPerWeek,
            undefined,
        );
    }, [hasErrorWorkingTimeClassicSpec, repetition, unitCount, missionCct]);

    const changeWorkingTime = useCallback(
        (_unitCount?: number, repetition?: Repetition) => {
            if (
                _unitCount !== undefined &&
                (isNaN(_unitCount) || _unitCount <= 0 || _unitCount > 24 * 7)
            ) {
                _unitCount = undefined;
            }
            setter({
                workingTime: {
                    repetition,
                    unit: Unit.Hours,
                    unitCount: _unitCount,
                },
            });
        },
        [setter],
    );

    const onModeChange = useCallback(
        (value: HourlyMode) => {
            setMode(value);
            switch (value) {
                case "on_demand":
                    setter({ workingTime: ON_DEMAND_WORKING_TIME_CLASSIC_SPEC });
                    break;
                case "punctual":
                    changeWorkingTime(unitCount, Repetition.None);
                    break;
                case "recurrent":
                    changeWorkingTime(unitCount, undefined);
                    break;
            }
        },
        [changeWorkingTime, setter, unitCount],
    );

    const modeItems = useMemo(
        () => [
            { label: i18n.t("utils.working_time.recurrent"), value: "recurrent" as const },
            { label: i18n.t("utils.working_time.punctual"), value: "punctual" as const },
            ...(allowOnDemand
                ? [
                      {
                          label: i18n.t("utils.working_time.on_demand.option"),
                          value: "on_demand" as const,
                      },
                  ]
                : []),
        ],
        [i18n, allowOnDemand],
    );

    const onUnitCountChange = useCallback(
        (value?: number) => {
            changeWorkingTime(value, repetition);
        },
        [repetition, changeWorkingTime],
    );

    if (!contract.workingTimeClassicSpec) {
        return null;
    }
    return (
        <VStack v="1/3" gap={0.5} style={{ paddingTop: 0 }}>
            <OptionsField<HourlyMode> value={mode} onSelect={onModeChange} data={modeItems} />
            {mode === "on_demand" && (
                <>
                    <Gap gap={0.5} />
                    <BodyMediumText>{i18n.t("utils.working_time.on_demand.info")}</BodyMediumText>
                </>
            )}
            {mode === "recurrent" && (
                <>
                    <Gap gap={0.5} />
                    <BodyMediumText>
                        {i18n.t("utils.working_time.repetition_info_hourly")}
                    </BodyMediumText>
                    <SelectField
                        onSelect={({ value }) => {
                            setContract({
                                ...contract,
                                workingTimeClassicSpec: {
                                    ...contract.workingTimeClassicSpec,
                                    repetition: value,
                                },
                            });
                        }}
                        placeholder={i18n.t("utils.working_time.repetition_label_hourly")}
                        data={repetitionItems}
                        value={contract.workingTimeClassicSpec?.repetition || undefined}
                    />
                </>
            )}

            {mode !== "on_demand" && (
                <>
                    <Gap gap={0.1} />
                    <BodyMediumText>{i18n.t("utils.working_time.hours_info")}</BodyMediumText>
                    <QuantityField
                        selectTextOnFocus
                        decimalPlaces={2}
                        style={{ alignSelf: "stretch" }}
                        disabled={disabled}
                        value={unitCount}
                        onChange={onUnitCountChange}
                        error={unitCount ? errorUnitCount : undefined}
                    />
                    {workingTime?.unitCount && workingTime?.repetition && (
                        <BodySmallText color={colors.fieldLabel}>
                            {formatWorkingTime(workingTime)}
                        </BodySmallText>
                    )}
                </>
            )}
        </VStack>
    );
};

export default HourlyClassicComponent;
